import { createContext, isValidElement, useContext, useId } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { Icon } from '../icons';

/** The message under an invalid field: an icon and one sentence saying what to do. */
export function FieldError({ id, message }: { id?: string; message?: string | null }) {
  if (!message) return null;
  return (
    <span id={id} className="onboarding-field__error">
      <Icon name="alert-circle" size={16} />
      <span>{message}</span>
    </span>
  );
}

/** What a `Field` hands the control inside it (read by `Input`, `Select` and `Textarea`). */
interface FieldWiring {
  id: string;
  /** The error's and the hint's ids, error first. */
  describedBy?: string;
  invalid: boolean;
}

const FieldContext = createContext<FieldWiring | null>(null);

/**
 * What a control takes from the `Field` around it (CO-177): the `id` the
 * label points at, the ids of the error and the hint for `aria-describedby`,
 * and `aria-invalid` while there is an error. Outside a `Field` it adds
 * nothing, and the control's own props stand.
 */
export function useFieldWiring(own: { 'aria-describedby'?: string; 'aria-invalid'?: boolean | 'true' | 'false' | 'grammar' | 'spelling' }) {
  const field = useContext(FieldContext);
  if (!field) return {};
  return {
    id: field.id,
    'aria-invalid': field.invalid ? true : own['aria-invalid'],
    'aria-describedby': [field.describedBy, own['aria-describedby']].filter(Boolean).join(' ') || undefined,
  };
}

/**
 * Field (CO-171, CO-177): the one way to put a label, a hint and an error on
 * a control. The control is the only child: an `Input`, `Select`, `Textarea`
 * or `PasswordInput`, which read what Field gives them (see `useFieldWiring`):
 *
 * - `id`: the child's own `id`, else the `id` prop, else a generated one;
 *   the label's `htmlFor` points at it, so the name never depends on nesting.
 * - `aria-describedby`: the error, then the hint, then whatever the control
 *   already had.
 * - `aria-invalid`: set while there is an `error`.
 *
 * The hint sits outside the label, so it describes the control instead of
 * becoming part of its name. A control that needs no visible label (a table
 * filter, a toolbar search) takes an `aria-label` and is not wrapped.
 *
 * Replaces the three label patterns that grew before it: the wrapping
 * `label.field`, the hand-written `div.field` + `label htmlFor`, and
 * `OnboardingField`.
 */
export function Field({
  label,
  hint,
  error,
  optional,
  action,
  id,
  className,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  /** After a failed submit: what is wrong and what to do. */
  error?: string | null;
  /** Adds "(optional)" after the label. */
  optional?: boolean;
  /** A button that acts on this field's value (replace a credential, issue
   * a new URL), under the hint. */
  action?: ReactNode;
  id?: string;
  className?: string;
  children: ReactElement;
}) {
  const generated = useId();
  const child = children as ReactElement<Record<string, unknown>>;
  const ownId = isValidElement(child) ? (child.props.id as string | undefined) : undefined;
  const controlId = id ?? ownId ?? generated;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const wiring: FieldWiring = { id: controlId, describedBy: [errorId, hintId].filter(Boolean).join(' ') || undefined, invalid: Boolean(error) };
  return (
    <div className={`field${className ? ` ${className}` : ''}`}>
      <label className="field__label" htmlFor={controlId}>
        {label}
        {optional ? (
          <>
            {' '}
            <span className="field__optional">(optional)</span>
          </>
        ) : null}
      </label>
      <FieldContext.Provider value={wiring}>{children}</FieldContext.Provider>
      {hint ? (
        <span id={hintId} className="field__hint">
          {hint}
        </span>
      ) : null}
      <FieldError id={errorId} message={error} />
      {action ? <div className="field__action">{action}</div> : null}
    </div>
  );
}
